
import React,{useState}from"react";
import{
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography
}from"@mui/material";
import{
  Visibility,
  VisibilityOff,
  KeyOutlined,
  LockResetRounded
}from"@mui/icons-material";
import{useLocation,useNavigate}from"react-router-dom";
import{useAuth}from"../context/AuthContext";
import logo from"../static/media/logo.webp";
import background from"../static/media/found.webp";

const PRIMARY="#65BDC3";
const PRIMARY_DARK="#368F96";
const TEXT="#243A40";
const SECONDARY_TEXT="#71848A";

export default function UpdatePasswordPage(){
  const location=useLocation();
  const navigate=useNavigate();
  const{accessToken,user,login}=useAuth();

  const[form,setForm]=useState({
    new_password:"",
    confirm_password:""
  });

  const[showPassword1,setShowPassword1]=useState(false);
  const[showPassword2,setShowPassword2]=useState(false);
  const[error,setError]=useState("");
  const[loading,setLoading]=useState(false);

  const getStoredValue=key=>{
    try{
      return localStorage.getItem(key)||"";
    }catch{
      return"";
    }
  };

  const token=
    location.state?.accessToken||
    accessToken||
    getStoredValue("accessToken")||
    getStoredValue("access")||
    getStoredValue("token")||
    "";

  const handleChange=event=>{
    const{name,value}=event.target;

    setForm(prev=>({
      ...prev,
      [name]:value
    }));
  };

  const parseJsonSafe=async response=>{
    try{
      return await response.json();
    }catch{
      return{};
    }
  };

  const updateLocalUser=async()=>{
    const nextUser={
      ...(user||{}),
      requires_password_change:false,
      requiere_cambio_clave:false,
      must_change_password:false,
      change_password_required:false,
      temporary_password:false
    };

    try{
      const storedUser=JSON.parse(
        getStoredValue("user")||"null"
      );

      if(storedUser){
        localStorage.setItem("user",JSON.stringify({
          ...storedUser,
          requires_password_change:false,
          requiere_cambio_clave:false,
          must_change_password:false,
          change_password_required:false,
          temporary_password:false
        }));
      }
    }catch{}

    if(typeof login==="function"){
      const refresh=
        location.state?.refreshToken||
        getStoredValue("refreshToken")||
        getStoredValue("refresh")||
        "";

      await login({
        access:token,
        refresh,
        user:nextUser
      });
    }

    return nextUser;
  };

  const submit=async event=>{
    event.preventDefault();
    setError("");

    if(!form.new_password||!form.confirm_password){
      setError("Todos los campos son obligatorios.");
      return;
    }

    if(form.new_password.length<8){
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    if(form.new_password!==form.confirm_password){
      setError("Las contraseñas no coinciden.");
      return;
    }

    if(!token){
      setError(
        "No se encontró el token de autenticación. Inicia sesión nuevamente para actualizar tu contraseña."
      );
      return;
    }

    try{
      setLoading(true);

      const response=await fetch("/api/auth/update-password/",{
        method:"POST",
        headers:{
          "Content-Type":"application/json",
          "Authorization":`Bearer ${token}`
        },
        body:JSON.stringify({
          new_password:form.new_password,
          confirm_password:form.confirm_password
        })
      });

      const data=await parseJsonSafe(response);

      if(!response.ok||data.ok===false){
        setError(
          data.error||
          data.detail||
          "Error al actualizar la contraseña."
        );
        return;
      }

      const nextUser=await updateLocalUser();
      const role=nextUser?.role||user?.role;

      if(["super_admin","admin","staff"].includes(role)){
        navigate("/admin-panel",{replace:true});
        return;
      }

      navigate("/",{replace:true});
    }catch(err){
      setError(
        err.message||
        "Error de conexión con el servidor."
      );
    }finally{
      setLoading(false);
    }
  };

  const inputStyles={
    "& .MuiOutlinedInput-root":{
      minHeight:48,
      borderRadius:"12px",
      backgroundColor:"#FFFFFF",
      fontSize:"0.92rem",
      fontWeight:400
    },
    "& .MuiOutlinedInput-notchedOutline":{
      borderColor:"#DCE6E8"
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":{
      borderColor:PRIMARY
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":{
      borderColor:PRIMARY_DARK,
      borderWidth:"1.5px"
    },
    "& .MuiInputBase-input":{
      color:TEXT,
      fontWeight:400
    },
    "& input:-webkit-autofill":{
      WebkitTextFillColor:TEXT,
      WebkitBoxShadow:"0 0 0 1000px #FFFFFF inset"
    }
  };

  return(
    <Box sx={{
      minHeight:"100vh",
      minHeight:"100dvh",
      width:"100%",
      display:"flex",
      alignItems:"center",
      justifyContent:"center",
      boxSizing:"border-box",
      overflowX:"hidden",
      px:{xs:2,sm:3},
      py:{xs:3,sm:4},
      backgroundImage:`
        linear-gradient(
          135deg,
          rgba(20,50,56,0.30) 0%,
          rgba(20,50,56,0.18) 50%,
          rgba(20,50,56,0.34) 100%
        ),
        url("${background}")
      `,
      backgroundSize:"cover",
      backgroundPosition:"center",
      backgroundRepeat:"no-repeat"
    }}>
      <Box
        component="form"
        onSubmit={submit}
        sx={{
          width:"100%",
          maxWidth:420,
          boxSizing:"border-box",
          px:{xs:2.5,sm:4},
          py:{xs:3.5,sm:4.5},
          borderRadius:{xs:"20px",sm:"24px"},
          backgroundColor:"rgba(255,255,255,0.96)",
          border:"1px solid rgba(255,255,255,0.85)",
          backdropFilter:"blur(18px)",
          WebkitBackdropFilter:"blur(18px)",
          boxShadow:"none"
        }}
      >
        <Box sx={{
          display:"flex",
          flexDirection:"column",
          alignItems:"center",
          mb:2.5
        }}>
          <Box
            component="img"
            src={logo}
            alt="Yumim"
            sx={{
              width:{xs:105,sm:120},
              height:{xs:105,sm:120},
              objectFit:"contain",
              display:"block",
              mb:1.5
            }}
          />

          <Typography sx={{
            color:PRIMARY_DARK,
            fontSize:"0.95rem",
            fontWeight:500,
            textAlign:"center"
          }}>
            ¡Sonríe, hay postre!
          </Typography>

          <Box sx={{
            width:44,
            height:3,
            borderRadius:"10px",
            bgcolor:PRIMARY,
            mt:2,
            mb:2
          }}/>

          <Typography
            component="h1"
            sx={{
              color:TEXT,
              fontSize:{xs:"1.5rem",sm:"1.75rem"},
              fontWeight:700,
              lineHeight:1.2,
              textAlign:"center",
              mb:1
            }}
          >
            Actualizar contraseña
          </Typography>

          <Typography sx={{
            color:SECONDARY_TEXT,
            fontSize:"0.87rem",
            lineHeight:1.6,
            fontWeight:400,
            textAlign:"center"
          }}>
            Ingresa una nueva contraseña para continuar.
          </Typography>
        </Box>

        {error&&(
          <Alert
            severity="error"
            sx={{
              mb:2.5,
              borderRadius:"12px",
              fontSize:"0.83rem",
              fontWeight:400,
              backgroundColor:"#FFF4F2",
              border:"1px solid #F3D2CD",
              "& .MuiAlert-message":{
                overflowWrap:"anywhere"
              }
            }}
          >
            {error}
          </Alert>
        )}

        <Box sx={{mb:2}}>
          <Typography
            component="label"
            htmlFor="new-password"
            sx={{
              display:"block",
              fontSize:"0.92rem",
              fontWeight:600,
              color:TEXT,
              mb:1
            }}
          >
            Nueva contraseña
          </Typography>

          <TextField
            id="new-password"
            fullWidth
            size="small"
            name="new_password"
            type={showPassword1?"text":"password"}
            value={form.new_password}
            onChange={handleChange}
            autoComplete="new-password"
            disabled={loading}
            required
            sx={inputStyles}
            InputProps={{
              startAdornment:(
                <InputAdornment position="start">
                  <KeyOutlined sx={{
                    fontSize:19,
                    color:"#93A6AA"
                  }}/>
                </InputAdornment>
              ),
              endAdornment:(
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    size="small"
                    disabled={loading}
                    aria-label={showPassword1?"Ocultar contraseña":"Mostrar contraseña"}
                    onClick={()=>setShowPassword1(prev=>!prev)}
                    sx={{color:"#83969A"}}
                  >
                    {showPassword1?(
                      <VisibilityOff sx={{fontSize:19}}/>
                    ):(
                      <Visibility sx={{fontSize:19}}/>
                    )}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>

        <Box sx={{mb:2.5}}>
          <Typography
            component="label"
            htmlFor="confirm-password"
            sx={{
              display:"block",
              fontSize:"0.92rem",
              fontWeight:600,
              color:TEXT,
              mb:1
            }}
          >
            Confirmar contraseña
          </Typography>

          <TextField
            id="confirm-password"
            fullWidth
            size="small"
            name="confirm_password"
            type={showPassword2?"text":"password"}
            value={form.confirm_password}
            onChange={handleChange}
            autoComplete="new-password"
            disabled={loading}
            required
            sx={inputStyles}
            InputProps={{
              startAdornment:(
                <InputAdornment position="start">
                  <LockResetRounded sx={{
                    fontSize:19,
                    color:"#93A6AA"
                  }}/>
                </InputAdornment>
              ),
              endAdornment:(
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    size="small"
                    disabled={loading}
                    aria-label={showPassword2?"Ocultar contraseña":"Mostrar contraseña"}
                    onClick={()=>setShowPassword2(prev=>!prev)}
                    sx={{color:"#83969A"}}
                  >
                    {showPassword2?(
                      <VisibilityOff sx={{fontSize:19}}/>
                    ):(
                      <Visibility sx={{fontSize:19}}/>
                    )}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disableElevation
          disabled={loading}
          sx={{
            minHeight:46,
            borderRadius:"12px",
            bgcolor:PRIMARY_DARK,
            color:"#FFFFFF",
            textTransform:"none",
            fontSize:"0.92rem",
            fontWeight:600,
            boxShadow:"none",
            "&:hover":{
              bgcolor:"#287D84",
              boxShadow:"none"
            },
            "&.Mui-disabled":{
              bgcolor:"#A5D4D7",
              color:"#FFFFFF"
            }
          }}
        >
          {loading?(
            <CircularProgress
              size={21}
              thickness={4}
              sx={{color:"#FFFFFF"}}
            />
          ):"Guardar contraseña"}
        </Button>

        {!token&&(
          <Button
            fullWidth
            variant="text"
            onClick={()=>navigate("/",{replace:true})}
            sx={{
              mt:1.5,
              textTransform:"none",
              fontSize:"0.85rem",
              fontWeight:500,
              color:PRIMARY_DARK
            }}
          >
            Volver al inicio de sesión
          </Button>
        )}

        <Typography sx={{
          mt:3,
          pt:2.5,
          borderTop:"1px solid #E9EFF0",
          fontSize:"0.74rem",
          fontWeight:400,
          color:"#91A0A4",
          lineHeight:1.6,
          textAlign:"center"
        }}>
          Acceso exclusivo para personal autorizado
        </Typography>
      </Box>
    </Box>
  );
}
